// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { node } from 'prop-types';
import useSWR, { useSWRConfig } from 'swr';
import noop from 'lodash/noop';

import { API_ORDERS } from 'Constants';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import { UserContext } from 'Contexts/UserContext';
import useBreadcrumbs from 'Hooks/useBreadcrumbs';

export const OrderContext = createContext({
  data: null,
  error: null,
  loading: false,
  mutate: noop,
  updateOrder: noop
});

const OrderProvider = ({ children }) => {
  const fetch = useFetch();
  const { cache } = useSWRConfig();
  const initialOrder = cache.get('order');
  const { params } = useRouter();
  const breadcrumbs = useBreadcrumbs();
  const user = useContextSelector(
    UserContext,
    data => data?.data || {}
  );
  const stateOrder = params.id === initialOrder?.id ? initialOrder : null;

  const [order, setOrder] = useState(stateOrder || {});
  const [isLoading, setIsLoading] = useState(params.id !== initialOrder?.id);

  const path = `${ API_ORDERS }/${ params.id }`;
  const response = useSWR(
    (
      params.id
      && user?.loggedIn
      && user?.email
    ) ? `${path}?uid=${user.email}` : null,
    {
      revalidateOnMount: !initialOrder || initialOrder.id !== params.id
    }
  );

  useEffect(() => {
    if (
      response?.data?.id
      && response.data.id !== initialOrder?.id
    ) {
      setIsLoading(false);
      cache.set('order', response.data);
      setOrder(response.data);
    }
  }, [response.data]);

  useEffect(() => {
    if (order?.id) {
      breadcrumbs.setBreadcrumbs(prevState => ({
        ...prevState,
        order
      }));
    } else {
      breadcrumbs.setBreadcrumbs(prevState => ({
        ...prevState,
        order: null
      }));
    }
  }, [order]);

  const orderContextValue = useMemo(
    () => ({
      data: order,
      mutate: data => {
        setOrder(data);
        response.mutate(data, false);
      },
      error: response.error,
      loading: isLoading,
      updateOrder: data => fetch(`${API_ORDERS}/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      }),
    }),
    [order, isLoading, response.error, response.mutate]
  );

  return (
    <OrderContext.Provider value={ orderContextValue }>
      { children }
    </OrderContext.Provider>
  );
};
OrderProvider.propTypes = {
  children: node
};
OrderProvider.displayName = 'OrderProvider';

export default OrderProvider;
